import { useEffect } from 'react';
import { useConfig } from '../context/ConfigContext';

const FALLBACK_LOGO = 'https://customer-assets.emergentagent.com/job_cooking-planner/artifacts/9loa2439_logo.png';

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Applique au runtime les métadonnées d'identité gérées dans le Configuration Center
 * (titre, description, favicon, Open Graph, mots-clés, theme-color). Toutes les valeurs
 * disposent d'un repli propre si elles ne sont pas renseignées.
 */
export default function SiteHead() {
  const { config } = useConfig();

  useEffect(() => {
    if (!config) return;
    const apply = () => {
      const seo = config.seo || {};
      const override = (typeof window !== 'undefined' && window.__pageSeo) || null;
      const companyName = config.company_name || 'Mitonner';
      const title = (override && override.title) || seo.site_title || `${companyName} - Cuisine à domicile`;
      const description = (override && override.description) || seo.meta_description || config.slogan || '';
      const keywords = seo.meta_keywords || '';
      const favicon = config.favicon_url || config.logo_url || FALLBACK_LOGO;
      const ogTitle = seo.og_title || title;
      const ogDescription = seo.og_description || description;
      const ogImage = config.og_image_url || seo.og_image || config.logo_url || FALLBACK_LOGO;
      const ogUrl = seo.canonical_url || (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : config.website);
      const themeColor = '#A5BBA8';

      document.title = title;
      upsertMeta('name', 'description', description);
      upsertMeta('name', 'keywords', keywords);
      upsertMeta('name', 'theme-color', themeColor);

      upsertMeta('property', 'og:title', ogTitle);
      upsertMeta('property', 'og:description', ogDescription);
      upsertMeta('property', 'og:image', ogImage);
      upsertMeta('property', 'og:url', ogUrl);
      upsertMeta('property', 'og:type', 'website');
      upsertMeta('property', 'og:site_name', companyName);

      upsertMeta('name', 'twitter:card', seo.twitter_card || 'summary_large_image');
      upsertMeta('name', 'twitter:title', ogTitle);
      upsertMeta('name', 'twitter:description', ogDescription);
      upsertMeta('name', 'twitter:image', ogImage);

      upsertLink('icon', favicon);
      upsertLink('apple-touch-icon', favicon);
      upsertLink('manifest', '/manifest.json');
      if (seo.canonical_url) upsertLink('canonical', seo.canonical_url);
    };

    // Application immédiate + ré-application différée pour rester résilient face au
    // script tiers qui mute le <head> au chargement.
    apply();
    const t1 = setTimeout(apply, 800);
    const t2 = setTimeout(apply, 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [config]);

  return null;
}
